<template>
    <div class="sr-date-range-picker" @click="closeTimezoneSlider">
        <custom-date-picker
            ref="dateRangeCalendar"
            :value="datePickerRange"
            :columns="columns"
            :first-day-of-week="2"
            :masks="masks"
            timezone="UTC"
            is-range
            trim-weeks
            @input="onDateRangeChange"
        >
            <template #default="{ inputValue, showPopover }">
                <div class="date-range">
                    <div class="date-range-fields pr-4">
                        <div class="date-box" @click.stop="showPopover">
                            <VTextField
                                :value="inputValue.start"
                                :class="classList"
                                :append-icon="isCalendarOpen ? 'mdi-menu-up' : 'mdi-menu-down'"
                                :outlined="true"
                                class="start-date-input"
                                label="Date From"
                                @change="validateAndUpdateDateRange(inputValue)"
                                @focus="isCalendarOpen = true"
                            >
                                <template #prepend-inner>
                                    <SrIcon icon="calendar" size="xs" class="mr-2" />
                                </template>
                            </VTextField>
                        </div>
                        <span class="pl-4 pr-4"> to </span>
                        <div class="date-box" @click.stop="showPopover">
                            <VTextField
                                :value="inputValue.end"
                                :class="classList"
                                :append-icon="isCalendarOpen ? 'mdi-menu-up' : 'mdi-menu-down'"
                                :outlined="true"
                                class="end-date-input"
                                label="Date To"
                                @change="validateAndUpdateDateRange(inputValue)"
                                @focus="isCalendarOpen = true"
                            >
                                <template #prepend-inner>
                                    <SrIcon icon="calendar" size="xs" class="mr-2" />
                                </template>
                            </VTextField>
                        </div>
                    </div>
                </div>
            </template>
        </custom-date-picker>
        <div class="date-range-customizations">
            <VCard class="pl-0 pr-0" elevation="0">
                <VBtnToggle
                    v-if="dateRangeConfigs.length"
                    ref="customConfigRanges"
                    v-model="selectedDateRangeConfigIndex"
                    class="custom-config-ranges"
                    color="primary accent-3"
                    group
                    @change="setConfigRange"
                >
                    <SrButton
                        v-for="(configRange, index) in dateRangeConfigs"
                        :key="index"
                        class="custom-border-btn ma-0 mr-1 custom-config-range"
                    >
                        {{ configRange.label }}
                    </SrButton>
                </VBtnToggle>
            </VCard>
            <VCard class="pl-1 pr-4" elevation="0">
                <VAutocomplete
                    v-if="lastNDaysRangeConfigs.length"
                    v-model="selectedLastNDaysRangeConfig"
                    :attach="true"
                    :clearable="true"
                    :items="lastNDaysRangeConfigs"
                    :outlined="true"
                    class="custom-day-ranges"
                    :class="customDayAgoRangeClassList"
                    label="Last N Days"
                    placeholder="Select..."
                    @change="setLastNDaysRange"
                />
            </VCard>
            <SrTimezoneSlider
                v-if="enableTimezones"
                :value="internalValue.timezoneInHours"
                :is-open="isTimezoneSliderOpen"
                @input="updateTimezone"
                @focus="closeCalendar"
                @isOpen="updateTimezoneIsOpen"
            />
        </div>
    </div>
</template>

<script>
import SrTimezoneSlider from './SrTimezoneSlider';
import { dateRangeProvider } from './DateRangeProvider';
import ZonedDateRange from './ZonedDateRange';
import { SrIcon } from '../SrIcon';
import { SrButton } from '../SrButton';
import UserTimezoneInformator from '../../utils/UserTimezoneInformator';
import { USER_INPUT_CLASS } from '../../utils/classNames';
import DateValidator from '../../utils/DateValidator';

export default {
    name: 'SrDateRangePicker',
    components: { SrTimezoneSlider, SrIcon, SrButton },
    props: {
        value: {
            type: ZonedDateRange,
            required: true,
        },
        /**
         * Selected config - one of dateRangeConfigs or lastNDaysRangeConfigs.
         */
        selectedRangeConfig: {
            type: Object,
            required: false,
        },
        /**
         * Define completely custom date range for selection.
         * @type DateRangeConfig
         */
        dateRangeConfigs: {
            type: Array,
            default: () => [],
        },
        /**
         * Define custom selections of last X days.
         * @type {
         *      text: string, // displayed text
         *      value: number // number of days to select
         * }
         */
        lastNDaysRangeConfigs: {
            type: Array,
            default: () => [],
        },
        startDate: {
            type: Date,
            default: () => new Date(),
        },
        endDate: {
            type: Date,
            default: () => new Date(),
        },
        timezoneInHours: {
            type: Number,
            default: () => UserTimezoneInformator.getUserTimezoneInWholeHours(),
        },
        /**
         * Define the date format.
         */
        masks: {
            type: Object,
            default: () => ({ input: 'YYYY-MM-DD' }),
        },
        enableTimezones: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            internalValue: this.getInitialValue(),
            selectedDateRangeConfigIndex: null,
            selectedLastNDaysRangeConfig: null,
            isCalendarOpen: false,
            isTimezoneSliderOpen: false,
        };
    },
    computed: {
        datePickerRange() {
            return {
                start: this.internalValue.start,
                end: this.internalValue.end,
            };
        },
        columns() {
            return this.$screens ? this.$screens({ default: 1, lg: 2 }) : [];
        },
        classList() {
            return [USER_INPUT_CLASS];
        },
        customDayAgoRangeClassList() {
            if (this.selectedLastNDaysRangeConfig) {
                return [USER_INPUT_CLASS, 'active'];
            }
            return [USER_INPUT_CLASS];
        },
    },
    watch: {
        selectedRangeConfig(newConfig) {
            this.onSelectedRangeConfigPropChange(newConfig);
        },
    },
    created() {
        this.onSelectedRangeConfigPropChange(this.selectedRangeConfig);
        this.emitInternalValue();
    },
    methods: {
        getInitialValue() {
            const clonedObject = this.value.clone();
            clonedObject.start = this.startDate;
            clonedObject.end = this.endDate;
            clonedObject.timezoneInHours = this.timezoneInHours;
            return clonedObject;
        },
        onDateRangeChange(datePickerRange) {
            const wasDateRangeChangedManually = this.isCalendarOpen;
            if (wasDateRangeChangedManually) {
                this.clearConfigSelections();
                this.closeCalendar();
                this.updateDateRange(datePickerRange);
                this.emitSelectedRangeConfig(null);
            }
        },
        updateDateRange(dateRange) {
            this.internalValue.setRange(new Date(dateRange.start), new Date(dateRange.end));
            this.emitInternalValue();
        },
        updateTimezone(timezoneInHours) {
            this.internalValue.timezoneInHours = timezoneInHours;
            this.emitInternalValue();
        },
        setConfigRange(index) {
            if (index == null) {
                return;
            }
            this.selectedLastNDaysRangeConfig = null;
            const selectedConfig = this.dateRangeConfigs[index];
            this.updateDateRangeWithDateRangeConfig(selectedConfig);
            this.emitSelectedRangeConfig(selectedConfig);
        },
        setLastNDaysRange(days) {
            this.selectedDateRangeConfigIndex = null;
            const selectedConfig = this.lastNDaysRangeConfigs.find((config) => config.value === days);
            if (selectedConfig) {
                this.updateDateRangeWithLastNDaysRangeConfig(selectedConfig);
            }
            this.emitSelectedRangeConfig(selectedConfig ?? null);
        },
        onSelectedRangeConfigPropChange(newConfig) {
            this.clearConfigSelections();
            if (this.isDateRangeConfig(newConfig)) {
                this.selectedDateRangeConfigIndex = this.dateRangeConfigs.findIndex((config) => newConfig.key === config.key);
                this.updateDateRangeWithDateRangeConfig(newConfig);
                return;
            }
            if (this.isLastNDaysRangeConfig(newConfig)) {
                this.selectedLastNDaysRangeConfig = newConfig;
                this.updateDateRangeWithLastNDaysRangeConfig(newConfig);
            }
        },
        validateAndUpdateDateRange(dateRange) {
            if (!this.validateDateRange(dateRange)) {
                return;
            }
            this.updateDateRange(dateRange);
            this.emitSelectedRangeConfig(null);
        },
        validateDateRange(dateRange) {
            return DateValidator.validateDateStrings([dateRange.start, dateRange.end]);
        },
        getConfigRange(config) {
            return dateRangeProvider.getPredefinedRange(config.key);
        },
        getLastNDaysRange(config) {
            return dateRangeProvider.getLastNDaysRange(config.value);
        },
        isLastNDaysRangeConfig(config) {
            return config?.value !== undefined;
        },
        isDateRangeConfig(config) {
            return config?.key !== undefined;
        },
        updateDateRangeWithDateRangeConfig(config) {
            const newRange = this.getConfigRange(config);
            this.updateDateRange(newRange);
        },
        updateDateRangeWithLastNDaysRangeConfig(config) {
            const newRange = this.getLastNDaysRange(config);
            this.updateDateRange(newRange);
        },
        updateTimezoneIsOpen(isOpen) {
            this.isTimezoneSliderOpen = isOpen;
        },
        closeCalendar() {
            this.$refs.dateRangeCalendar?.hidePopover();
            this.isCalendarOpen = false;
        },
        closeTimezoneSlider() {
            this.isTimezoneSliderOpen = false;
        },
        clearConfigSelections() {
            this.selectedLastNDaysRangeConfig = null;
            this.selectedDateRangeConfigIndex = null;
        },
        emitInternalValue() {
            this.$emit('input', this.internalValue.clone());
        },
        emitSelectedRangeConfig(config) {
            this.$emit('configChange', config);
        },
    },
};
</script>
